import React, { useContext } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@material-ui/core";

import { WhatsAppsContext } from "../../../context/WhatsApp/WhatsAppsContext";
import { parseTemplate } from "../../WabaTemplateModal/helpers";
import useCampaignTemplates from "../../CampaignModal/hooks/useCampaignTemplates";

const isWaba = (p) => String(p || "").toLowerCase() === "waba";

// Config del nodo "Plantilla HSM" (0371 WF2). Elige conexión WABA + plantilla APPROVED y
// mapea las variables (admiten variables del flujo: {{name}}, {{email}}...). Reutiliza
// useCampaignTemplates (carga de plantillas) y parseTemplate (detección de variables).
export default function HsmTemplateConfig({ nodeData, setNodeData }) {
  const { whatsApps } = useContext(WhatsAppsContext);
  const wabaConnections = (whatsApps || []).filter((w) => isWaba(w.provider));

  const whatsappId = nodeData.whatsappId || "";
  const templateId = nodeData.templateId || "";
  const { templates, loading } = useCampaignTemplates(whatsappId);

  const selected = templates.find((t) => String(t.id) === String(templateId));
  const parsed = selected ? parseTemplate(selected.components) : null;

  const headerIsMedia = parsed?.header?.isMedia;
  const headerVarCount = parsed?.header && !headerIsMedia ? parsed.header.varCount : 0;
  const bodyVarCount = parsed?.body?.varCount || 0;

  const patch = (p) => setNodeData({ ...nodeData, ...p });

  const handleSelectConnection = (id) => {
    // Cambiar de conexión invalida la plantilla y sus variables.
    patch({ whatsappId: id, templateId: "", templateName: "", language: "", variables: { header: [], body: [] } });
  };

  const handleSelectTemplate = (id) => {
    const tpl = templates.find((t) => String(t.id) === String(id));
    const p = tpl ? parseTemplate(tpl.components) : null;
    const hCount = p?.header && !p.header.isMedia ? p.header.varCount : 0;
    const bCount = p?.body?.varCount || 0;
    patch({
      templateId: id,
      templateName: tpl?.name || "",
      language: tpl?.language || "",
      variables: {
        header: p?.header?.isMedia ? [""] : Array.from({ length: hCount }, () => ""),
        body: Array.from({ length: bCount }, () => ""),
      },
    });
  };

  const setVar = (which, i, value) => {
    const arr = [...((nodeData.variables && nodeData.variables[which]) || [])];
    arr[i] = value;
    patch({ variables: { ...(nodeData.variables || {}), [which]: arr } });
  };

  return (
    <Box p={1}>
      <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 16 }}>
        <InputLabel>Conexión WABA</InputLabel>
        <Select
          label="Conexión WABA"
          value={whatsappId}
          onChange={(e) => handleSelectConnection(e.target.value)}
        >
          {wabaConnections.length === 0 && (
            <MenuItem value="" disabled>No hay conexiones WABA conectadas</MenuItem>
          )}
          {wabaConnections.map((w) => (
            <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {whatsappId && (
        <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 16 }}>
          <InputLabel>Plantilla aprobada</InputLabel>
          <Select
            label="Plantilla aprobada"
            value={templateId}
            onChange={(e) => handleSelectTemplate(e.target.value)}
          >
            {loading && <MenuItem value="" disabled>Cargando plantillas...</MenuItem>}
            {!loading && templates.length === 0 && (
              <MenuItem value="" disabled>No hay plantillas aprobadas en esta conexión</MenuItem>
            )}
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name} ({t.language})</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {parsed && (
        <>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
            {"Valores de las variables (puedes usar variables del flujo: {{name}}, {{email}}, {{number}})"}
          </Typography>

          {headerIsMedia && (
            <TextField
              fullWidth size="small" variant="outlined" margin="dense"
              label="URL del archivo del encabezado (https://...)"
              value={nodeData.variables?.header?.[0] || ""}
              onChange={(e) => setVar("header", 0, e.target.value)}
            />
          )}

          {!headerIsMedia && Array.from({ length: headerVarCount }).map((_, i) => (
            <TextField
              key={`h-${i}`}
              fullWidth size="small" variant="outlined" margin="dense"
              label={`Encabezado {{${parsed.header.varKeys?.[i] ?? i + 1}}}`}
              value={nodeData.variables?.header?.[i] || ""}
              onChange={(e) => setVar("header", i, e.target.value)}
            />
          ))}

          {Array.from({ length: bodyVarCount }).map((_, i) => (
            <TextField
              key={`b-${i}`}
              fullWidth size="small" variant="outlined" margin="dense"
              label={`Cuerpo {{${parsed.body.varKeys?.[i] ?? i + 1}}}`}
              value={nodeData.variables?.body?.[i] || ""}
              onChange={(e) => setVar("body", i, e.target.value)}
            />
          ))}

          {headerVarCount === 0 && bodyVarCount === 0 && !headerIsMedia && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Esta plantilla no tiene variables. Se enviará tal cual.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
